import React, { useEffect, useState, useRef } from 'react'
import { Button, Form, Spinner, Table } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { GetAllServiceDefinitionReq, ServiceDefinitionDto } from '../../../interfaces/serviceDefinition/service-definition';
import { DeleteServiceDefinitionAsync, GetAllServicesAsync } from '../../../services/ServiceDefinition/ServiceDefinitionService';
import { useAuth } from '../../../firebase/AuthProvider';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import Pagination from '../../ui/Pagination/Pagination';
import { PaginationDto } from '../../../interfaces/Employee/Employee';
import { PAGINATION } from '../../../Data/SD';
import { SortState } from '../../../interfaces/commonModals';
import { getSortIcon } from '../../../utilities/common/commonFuntion';
import editicons from '../../../assets/images/editicons.svg';
import trashicons from '../../../assets/images/trashicons.svg';
import { showConfirmationDialog } from '../../modal/ConfimationModal/Confimation';

const ParticipantServiceDefinitionPage = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    // Data
    const [services, setServices] = useState<ServiceDefinitionDto[]>([]);


    // Pagination
    const [serviceCount, setServiceCount] = useState<number>();
    const [pagination, setPagination] = useState<PaginationDto>({ pageNumber: PAGINATION.PageNumber, pageSize: PAGINATION.PageSize });

    // sort 
    const [sort, setSort] = useState<SortState>({ sortBy: null, sortDirection: "asc" });

    // search
    const [searchTerm, setSearchTerm] = useState<string>("");
    const searchTimeout = useRef<NodeJS.Timeout | null>(null);

    // loading
    const [loading, setLoading] = useState({
        getServiceLoading: false,
        deleteServiceId: ""
    });
    const [relaod, setReload] = useState<boolean>(true);


    useEffect(() => {
        GetAllServices();
        // eslint-disable-next-line
    }, [pagination, sort, searchTerm, relaod]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            setSearchTerm(value);
            setPagination(prev => ({ ...prev, pageNumber: 1 })); // Reset to first page on search
        }, 500);
    };

    const GetAllServices = async () => {
        const token = await currentUser?.getIdToken();
        if (!token) return;

        const payload: GetAllServiceDefinitionReq = {
            searchText: searchTerm || null,
            serviceTypeCodeId: null,
            pageNumber: pagination.pageNumber,
            pageSize: pagination.pageSize,
            active: true,
            sortBy: sort.sortBy,
            sortDirection: sort.sortDirection
        };
        try {
            setLoading(prev => ({ ...prev, getServiceLoading: true }));
            const res = await GetAllServicesAsync(token, payload);
            if (res.success) {
                setServices(res.data.items);
                setServiceCount(res.data.totalCount);
            } else {
                toast.error(res.message);
            }
        } catch (error: any) {
            toast.error(error instanceof AxiosError ? error.response?.data.message : error.message || "Error to Get the Services");
        } finally {
            setLoading(prev => ({ ...prev, getServiceLoading: false }));
        }
    };

    // Sorting handler
    const handleSort = (field: string) => {
        setSort(prev => {
            if (prev.sortBy === field) {
                return { ...prev, sortDirection: prev.sortDirection === "asc" ? "desc" : "asc" };
            } else {
                return { sortBy: field, sortDirection: "asc" };
            }
        });
    };

    const deleteServiceDefinition = async (serviceId: string) => {
        const token = await currentUser?.getIdToken();
        const isConfirm = await showConfirmationDialog();
        if (!token || !isConfirm) return;

        try {
            setLoading(prev => ({ ...prev, deleteServiceId: serviceId }));
            const res = await DeleteServiceDefinitionAsync(token, serviceId);
            if (res.success) {
                setReload(!relaod);
                toast.success(res.message);
            } else {
                toast.error(res.message);
            }
        } catch (error: any) {
            toast.error(error instanceof AxiosError ? error.response?.data.message : error.message || "Error to Delete the Services");
        } finally { setLoading(prev => ({ ...prev, deleteServiceId: "" })); }
    }

    const onPageChange = (pageNum: number) => {
        setPagination((prev) => ({
            ...prev,
            pageNumber: pageNum
        }))
    }

    const resetPageSize = (pageNumber: number, pageSize: number) => {
        setPagination({
            pageNumber: pageNumber,
            pageSize: pageSize
        })
    }

    return (
        <>
            <div className='common_card'>
                <div className="top top_bar_flx" >
                    <div className="top_bar_tittle">
                        <h2>Service Definition</h2>
                    </div>
                    <div className="top_right_flx">
                        {/* Search bar with debounce */}
                        <Form className="d-flex align-items-center me-2" onSubmit={e => e.preventDefault()}>
                            <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Search..."
                                style={{ minWidth: 220 }}
                                className="me-2 py-0"
                                onChange={handleSearchChange}
                            />
                        </Form>
                        <Button
                            variant="primary"
                            className="btn_sm"
                            onClick={() =>
                                navigate(
                                    "/dashboard/owner/service-definition/Add"
                                )
                            }
                        >
                            Add New Service
                        </Button>
                    </div>
                </div >
                <Table className="common_table">
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('code')}>
                                Code {getSortIcon(sort.sortBy === 'code', sort.sortDirection)}
                            </th>
                            <th className='cursor-pointer' onClick={() => handleSort('name')}>
                                Name {getSortIcon(sort.sortBy === 'name', sort.sortDirection)}
                            </th>
                            <th className='cursor-pointer'>Description</th>
                            <th onClick={() => handleSort('service_type')}>
                                Service Type {getSortIcon(sort.sortBy === 'service_type', sort.sortDirection)}
                            </th>
                            <th className='cursor-pointer' onClick={() => handleSort('frequency_type')}>
                                Frequency Type {getSortIcon(sort.sortBy === 'frequency_type', sort.sortDirection)}
                            </th>
                            <th className='cursor-pointer' onClick={() => handleSort('unit_type')}>
                                Unit Type {getSortIcon(sort.sortBy === 'unit_type', sort.sortDirection)}
                            </th>
                            <th>Planned Units</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            loading.getServiceLoading
                                ? <tr><td colSpan={9} className='text-center'><Spinner size='sm' /> Loading...</td></tr>
                                : services.length === 0
                                    ? <tr><td colSpan={9} className='text-center'>No Data Found</td></tr>
                                    : (services.map((ser) => (
                                        <tr key={ser.id}>
                                            <td>{ser.code}</td>
                                            <td className='showShortTextCollapse' title={ser.name}>{ser.name}</td>
                                            <td className='showShortTextCollapse' title={ser.description}>{ser.description}</td>
                                            <td>{ser.serviceType?.name}</td>
                                            <td>{ser.defaultFrequencyType?.name}</td>
                                            <td>{ser.defaultUnitType?.name}</td>
                                            <td>{ser.defaultPlannedUnits}</td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <div className="action_btn">
                                                    <span>
                                                        <img src={editicons} alt="editicons" onClick={()=> navigate(`/dashboard/owner/service-definition/Edit/${ser.id}`)} />
                                                    </span>
                                                    <span>
                                                        {loading.deleteServiceId == ser.id ? <Spinner size='sm' /> : <img src={trashicons} alt="trashicons" onClick={() => deleteServiceDefinition(ser.id)} />}
                                                    </span>

                                                </div>
                                            </td>
                                        </tr>
                                    )))
                        }
                    </tbody>
                </Table>

                {(serviceCount && serviceCount > 0) ? (
                    <Pagination
                        currentPage={pagination.pageNumber}
                        totalPages={Math.ceil(serviceCount / pagination.pageSize)}
                        onPageChange={onPageChange}
                        pageSize={pagination.pageSize}
                        resetPageSize={resetPageSize}
                    />
                ) : <></>}

            </div>
        </>
    );
}

export default ParticipantServiceDefinitionPage